// src/components/MentorSatisfactionChart.js
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { useTranslation } from 'react-i18next';

const MentorSatisfactionChart = ({ surveyId = null }) => {
  const { t } = useTranslation();

  const [trendData, setTrendData] = useState([]);
  const [criticalRows, setCriticalRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const makeParams = () => {
    const n = Number(surveyId);
    if (!Number.isInteger(n) || n <= 0) return undefined;
    // unterstützt beide Varianten im Backend (survey_id / surveyId)
    return { survey_id: n, surveyId: n };
  };

  // Daten laden: Verlauf + kritisch bewertete Mentor:innen
  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      setLoading(true);
      try {
        const params = makeParams();

        const [trendRes, criticalRes] = await Promise.all([
          axios.get('/mentoring/feedback/mentor-satisfaction-trend', {
            withCredentials: true,
            params
          }),
          axios
            .get('/mentoring/mentors/low-satisfaction', {
              withCredentials: true,
              params
            })
            .catch((err) => {
              console.error(
                'Fehler beim Laden /mentors/low-satisfaction:',
                err
              );
              return { data: [] };
            })
        ]);

        if (!isMounted) return;

        setTrendData(Array.isArray(trendRes.data) ? trendRes.data : []);
        setCriticalRows(Array.isArray(criticalRes.data) ? criticalRes.data : []);
      } catch (err) {
        if (!isMounted) return;
        console.error(
          'Fehler beim Laden der Mentor-Zufriedenheitsdaten:',
          err
        );
        setTrendData([]);
        setCriticalRows([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [surveyId]); // <-- wichtig

  // Milestone → Label (DE)
  const milestoneLabels = {
    kennenlerntermin: t('milestoneIntro'),
    '3mon': t('milestone3'),
    '6mon': t('milestone6'),
    '9mon': t('milestone9'),
    '12mon': t('milestone12')
  };

  // Chart-Daten vorbereiten
  const chartData = trendData.map((row) => ({
    milestone: milestoneLabels[row.milestone] || row.milestone,
    average: row.average != null ? Number(row.average) : null,
    count: Number(row.count) || 0
  }));

  // Gesamtanzahl Bewertungen über alle Milestones
  const totalReviewCount = chartData.reduce((sum, d) => sum + d.count, 0);

  // Kritische Mentor:innen (distinct)
  const criticalMentors = useMemo(() => {
    const map = new Map();

    for (const row of criticalRows) {
      const id = row.mentor_id || row.user_id;
      if (!id) continue;

      if (!map.has(id)) {
        map.set(id, {
          mentor_id: id,
          first_name: row.first_name,
          last_name: row.last_name,
          satisfaction_score: row.satisfaction_score,
          milestone: row.milestone,
          submitted_at: row.submitted_at
        });
      }
    }

    return Array.from(map.values());
  }, [criticalRows]);

  const criticalCount = criticalMentors.length;

  // Chat öffnen (HR → Mentor)
  const openChat = (mentorId, firstName, lastName) => {
    const presetMessage =
      `Hallo ${firstName || ''} ${lastName || ''}`.trim() +
      ', ich habe mir dein letztes Feedback angeschaut ' +
      'und würde gerne kurz mit dir darüber sprechen.';

    window.dispatchEvent(
      new CustomEvent('openChatPopup', {
        detail: {
          menteeId: null,
          mentorId,
          senderRole: 'hr',
          receiverRole: 'mentor',
          presetMessage,
          context: {
            source: 'mentor-satisfaction',
            forceAppHandling: true
          }
        }
      })
    );
  };

  return (
    <>
      <div className="bg-gray-800 rounded-2xl p-6 shadow-md relative">
        {/* Titel + Badge */}
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-white text-lg font-bold">
            {t('mentorSatisfactionTitle') ||
              'Zufriedenheit der Mentoren (Verlauf)'}
          </h3>

          {criticalCount > 0 && (
            <button
              type="button"
              onClick={() => setShowModal(true)}
              className="flex items-center gap-2 bg-red-600 text-white text-xs font-semibold px-3 py-1 rounded-full shadow hover:bg-red-500 transition"
            >
              <span className="bg-white text-red-600 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
                {criticalCount}
              </span>
              <span>{t('criticalLabel') || 'kritisch'}</span>
            </button>
          )}
        </div>

        {/* Chart-Bereich */}
        <div className="h-64">
          {loading ? (
            <div className="flex items-center justify-center h-full text-gray-300 text-sm">
              {t('loading') || 'Lade Daten…'}
            </div>
          ) : chartData.length === 0 ? (
            <div className="flex items-center justify-center h-full text-gray-400 text-sm">
              {t('noData') || 'Noch keine Feedbackdaten vorhanden.'}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#3b4252" />
                <XAxis
                  dataKey="milestone"
                  stroke="#e5e9f0"
                  tick={{ fontSize: 12 }}
                />
                <YAxis
                  domain={[1, 5]}
                  allowDecimals={false}
                  stroke="#e5e9f0"
                  tick={{ fontSize: 12 }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1f2933',
                    borderRadius: '0.75rem',
                    border: '1px solid #4b5563'
                  }}
                  labelStyle={{ color: '#e5e9f0' }}
                />
                <Legend
                  verticalAlign="bottom"
                  height={32}
                  wrapperStyle={{ color: '#e5e9f0', fontSize: 12 }}
                />
                <Line
                  type="monotone"
                  dataKey="average"
                  name={t('satisfactionLabelMentor') || 'Zufriedenheit'}
                  stroke="#60a5fa"
                  strokeWidth={3}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {!loading && totalReviewCount > 0 && (
          <p className="text-xs text-gray-400 mt-2 text-right">
            {totalReviewCount === 1
              ? t('basedOnOneReview', 'Basierend auf 1 Bewertung')
              : t('basedOnReviews', 'Basierend auf {{count}} Bewertungen', { count: totalReviewCount })}
          </p>
        )}
      </div>

      {/* Modal: Kritisch bewertete Mentor:innen */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-gray-900 rounded-2xl p-6 w-full max-w-3xl shadow-xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-white text-lg font-bold">
                {t('showCriticalMentors') ||
                  'Kritisch bewertete Mentor:innen anzeigen'}
              </h4>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="text-gray-300 hover:text-white text-sm px-2 py-1"
              >
                ✕
              </button>
            </div>

            {criticalMentors.length === 0 ? (
              <div className="text-gray-300 text-sm">
                {t('noCriticalMentors') ||
                  'Aktuell keine kritisch bewerteten Mentor:innen.'}
              </div>
            ) : (
              <div className="space-y-4">
                {criticalMentors.map((m) => (
                  <div
                    key={m.mentor_id}
                    className="border border-gray-700 rounded-xl p-4 bg-gray-800"
                  >
                    <div className="flex justify-between items-center mb-2">
                      <div>
                        <div className="text-white font-semibold">
                          {m.first_name} {m.last_name}
                        </div>
                        <div className="text-gray-400 text-xs">
                          {t('criticalSatisfactionSubtitle') ||
                            'Letztes Feedback unterhalb des gewünschten Niveaus.'}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          openChat(m.mentor_id, m.first_name, m.last_name)
                        }
                        className="text-xs bg-green-500 hover:bg-green-400 text-white font-semibold px-3 py-1 rounded-full shadow"
                      >
                        Nachricht an Mentor:in
                      </button>
                    </div>

                    <div className="text-gray-200 text-xs">
                      <span className="font-semibold">Score:</span>{' '}
                      {m.satisfaction_score}{' '}
                      {m.milestone &&
                        `(${milestoneLabels[m.milestone] || m.milestone})`}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="bg-gray-700 hover:bg-gray-600 text-white text-sm font-medium px-4 py-2 rounded-lg"
              >
                {t('close') || 'Schließen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MentorSatisfactionChart;
